import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { api, resolveFileUrl } from '../services/api';
import useCurrency from '../hooks/useCurrency';
import { useI18n } from '../hooks/useI18n';
import { useTheme } from '../hooks/useTheme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator,
  faBoxOpen,
  faDollarSign,
  faChartLine,
  faArrowDown,
  faEye,
  faPlay,
  faTimes,
  faPlus,
  faExclamationTriangle,
  faCheckCircle,
  faFileExport,
  faSyncAlt,
  faBan,
  faBalanceScale,
  faReceipt,
  faGavel,
  faTrashAlt,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faSortAmountDown,
  faSortAmountUp,
  faTag,
  faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Accounting.css';

/* ──────────────────────────── Types ──────────────────────────── */

interface AssetAccount {
  id: string;
  itemId: string;
  assetNumber?: string;
  assetClass?: string;
  subClass?: string;
  acquisitionCost: number;
  acquisitionDate: string;
  bookValue: number;
  accumulatedDepreciation: number;
  fairMarketValue?: number;
  salvageValue: number;
  depreciationMethod: string;
  usefulLifeMonths: number;
  status: string;
  verificationStatus: string;
  createdAt?: string;
  item?: {
    id: string;
    name: string;
    description?: string;
    imageUrl?: string;
    images?: { url: string }[];
    category?: { name: string; color?: string };
    condition?: string;
    location?: string;
  };
  depreciation?: {
    monthlyDepreciation: number;
    yearlyDepreciation: number;
    remainingLifeMonths: number;
    percentDepreciated: number;
  };
  depreciationInfo?: {
    monthlyDepreciation: number;
    yearlyDepreciation: number;
    remainingLifeMonths: number;
    percentDepreciated: number;
  };
  transactions?: any[];
  depreciationSchedule?: any[];
}

type Tab = 'register' | 'items' | 'transactions';
type SortDir = 'asc' | 'desc';

const METHOD_KEYS: Record<string, string> = {
  STRAIGHT_LINE: 'accounting.straightLine',
  DECLINING_BALANCE: 'accounting.decliningBalance',
  DOUBLE_DECLINING: 'accounting.doubleDeclining',
  SUM_OF_YEARS: 'accounting.sumOfYears',
  UNITS_OF_PRODUCTION: 'accounting.unitsOfProduction',
  NONE: 'accounting.noDepreciation',
};

const STATUS_CLASS: Record<string, string> = {
  ACTIVE: 'active',
  DISPOSED: 'disposed',
  WRITTEN_OFF: 'written-off',
  HELD_FOR_SALE: 'held',
  IMPAIRED: 'impaired',
  TRANSFERRED: 'held',
};

const ITEMS_PER_PAGE = 25;

/* ──────────────────────────── Component ──────────────────────────── */

export default function Accounting() {
  const { user } = useSelector((state: RootState) => state.auth);
  const userCurrency = (user as any)?.defaultCurrency || 'USD';
  const { formatPrice } = useCurrency(userCurrency);
  const { t } = useI18n();
  const { theme } = useTheme();

  const [tab, setTab] = useState<Tab>('register');
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState<AssetAccount[]>([]);
  const [allItems, setAllItems] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<AssetAccount | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // KPI
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalBookValue, setTotalBookValue] = useState(0);
  const [totalDepreciation, setTotalDepreciation] = useState(0);
  const [totalCost, setTotalCost] = useState(0);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImpairmentModal, setShowImpairmentModal] = useState(false);
  const [showRevalModal, setShowRevalModal] = useState(false);
  const [showDisposeModal, setShowDisposeModal] = useState(false);
  const [modalAssetId, setModalAssetId] = useState<string>('');

  // ── Register tab filters & search ──
  const [regSearch, setRegSearch] = useState('');
  const [regStatus, setRegStatus] = useState('all');
  const [regMethod, setRegMethod] = useState('all');
  const [regClass, setRegClass] = useState('all');
  const [regSort, setRegSort] = useState<'name' | 'cost' | 'book' | 'dep' | 'date'>('name');
  const [regSortDir, setRegSortDir] = useState<SortDir>('asc');
  const [regPage, setRegPage] = useState(1);

  // ── Items tab filters & search ──
  const [itemSearch, setItemSearch] = useState('');
  const [itemCategory, setItemCategory] = useState('all');
  const [itemCondition, setItemCondition] = useState('all');
  const [itemTracked, setItemTracked] = useState<'all' | 'tracked' | 'untracked'>('all');
  const [itemSort, setItemSort] = useState<'name' | 'price' | 'date'>('name');
  const [itemSortDir, setItemSortDir] = useState<SortDir>('asc');
  const [itemPage, setItemPage] = useState(1);

  // ── Transactions tab search ──
  const [txSearch, setTxSearch] = useState('');
  const [txType, setTxType] = useState('all');
  const [txPage, setTxPage] = useState(1);

  // Create form
  const [createForm, setCreateForm] = useState({
    itemId: '',
    assetClass: '',
    acquisitionCost: '',
    acquisitionDate: new Date().toISOString().split('T')[0],
    salvageValue: '0',
    depreciationMethod: 'STRAIGHT_LINE',
    usefulLifeMonths: '60',
  });
  const [creating, setCreating] = useState(false);

  // Impairment form
  const [impairForm, setImpairForm] = useState({ impairmentAmount: '', reason: '', effectiveDate: new Date().toISOString().split('T')[0] });
  // Reval form
  const [revalForm, setRevalForm] = useState({ newValue: '', appraiserName: '', valuationMethod: '', notes: '' });
  // Dispose form
  const [disposeForm, setDisposeForm] = useState({ disposalAmount: '0', reason: 'SOLD', effectiveDate: new Date().toISOString().split('T')[0] });

  /* ═══════════════  DATA FETCHING  ═══════════════ */

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/accounting/assets', { params: { take: 500 } });
      const payload = res.data;
      // Backend returns { accounts, total }
      const list: AssetAccount[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.accounts)
          ? payload.accounts
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

      setAssets(list);
      setTotalAssets(list.length);
      setTotalBookValue(list.reduce((s, a) => s + (a.bookValue || 0), 0));
      setTotalDepreciation(list.reduce((s, a) => s + (a.accumulatedDepreciation || 0), 0));
      setTotalCost(list.reduce((s, a) => s + (a.acquisitionCost || 0), 0));
    } catch (err: any) {
      console.error('Failed to load assets', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/items', { params: { take: 1000 } });
      const payload = res.data;
      const items = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : [];
      setAllItems(items);
    } catch (err) {
      console.error('Failed to load items', err);
    }
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);
  useEffect(() => { if (tab === 'items') fetchItems(); }, [tab, fetchItems]);

  // Auto-dismiss messages
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(null), 5000); return () => clearTimeout(t); }
  }, [error]);
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(null), 5000); return () => clearTimeout(t); }
  }, [success]);

  // Reset page on filter change
  useEffect(() => { setRegPage(1); }, [regSearch, regStatus, regMethod, regClass, regSort, regSortDir]);
  useEffect(() => { setItemPage(1); }, [itemSearch, itemCategory, itemTracked, itemSort, itemSortDir]);
  useEffect(() => { setTxPage(1); }, [txSearch, txType]);

  /* ═══════════════  HELPERS  ═══════════════ */

  const fmt = (n: number | undefined | null) => formatPrice(n ?? 0, userCurrency);
  const pct = (v: number | undefined | null) => `${(v ?? 0).toFixed(1)}%`;
  const depPct = (a: AssetAccount) => {
    if (!a.acquisitionCost || a.acquisitionCost === 0) return 0;
    return Math.min(((a.accumulatedDepreciation || 0) / a.acquisitionCost) * 100, 100);
  };
  const depClass = (p: number) => p < 40 ? 'low' : p < 75 ? 'mid' : 'high';
  const dateStr = (d: string | undefined | null) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const getItemThumb = (item: any) => {
    if (item?.imageUrl) return resolveFileUrl(item.imageUrl);
    if (item?.images?.[0]?.url) return resolveFileUrl(item.images[0].url);
    return null;
  };

  const trackedItemIds = useMemo(() => new Set(assets.map(a => a.itemId)), [assets]);

  /* ═══════════════  FILTERED / SORTED DATA  ═══════════════ */

  // Unique categories from items
  const categories = useMemo(() => {
    const cats = new Set<string>();
    allItems.forEach(i => { if (i.category?.name) cats.add(i.category.name); });
    return Array.from(cats).sort();
  }, [allItems]);

  // Unique classes from assets
  const assetClasses = useMemo(() => {
    const cls = new Set<string>();
    assets.forEach(a => { if (a.assetClass) cls.add(a.assetClass); });
    return Array.from(cls).sort();
  }, [assets]);

  // ── Register tab filtered ──
  const filteredAssets = useMemo(() => {
    let list = [...assets];
    const q = regSearch.toLowerCase().trim();
    if (q) list = list.filter(a =>
      (a.item?.name || '').toLowerCase().includes(q) ||
      (a.assetNumber || '').toLowerCase().includes(q) ||
      (a.assetClass || '').toLowerCase().includes(q)
    );
    if (regStatus !== 'all') list = list.filter(a => a.status === regStatus);
    if (regMethod !== 'all') list = list.filter(a => a.depreciationMethod === regMethod);
    if (regClass !== 'all') list = list.filter(a => a.assetClass === regClass);

    list.sort((a, b) => {
      let cmp = 0;
      switch (regSort) {
        case 'name': cmp = (a.item?.name || '').localeCompare(b.item?.name || ''); break;
        case 'cost': cmp = (a.acquisitionCost || 0) - (b.acquisitionCost || 0); break;
        case 'book': cmp = (a.bookValue || 0) - (b.bookValue || 0); break;
        case 'dep': cmp = depPct(a) - depPct(b); break;
        case 'date': cmp = new Date(a.acquisitionDate || 0).getTime() - new Date(b.acquisitionDate || 0).getTime(); break;
      }
      return regSortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [assets, regSearch, regStatus, regMethod, regClass, regSort, regSortDir]);

  const regTotalPages = Math.max(1, Math.ceil(filteredAssets.length / ITEMS_PER_PAGE));
  const pagedAssets = filteredAssets.slice((regPage - 1) * ITEMS_PER_PAGE, regPage * ITEMS_PER_PAGE);

  // ── Items tab filtered ──
  const filteredItems = useMemo(() => {
    let list = [...allItems];
    const q = itemSearch.toLowerCase().trim();
    if (q) list = list.filter(i =>
      (i.name || '').toLowerCase().includes(q) ||
      (i.description || '').toLowerCase().includes(q) ||
      (i.location || '').toLowerCase().includes(q) ||
      (i.category?.name || '').toLowerCase().includes(q) ||
      ((i as any).tags || []).some((tag: string) => tag.toLowerCase().includes(q))
    );
    if (itemCategory !== 'all') list = list.filter(i => i.category?.name === itemCategory);
    if (itemCondition !== 'all') list = list.filter(i => (i.condition || '') === itemCondition);
    if (itemTracked === 'tracked') list = list.filter(i => trackedItemIds.has(i.id));
    if (itemTracked === 'untracked') list = list.filter(i => !trackedItemIds.has(i.id));

    list.sort((a, b) => {
      let cmp = 0;
      switch (itemSort) {
        case 'name': cmp = (a.name || '').localeCompare(b.name || ''); break;
        case 'price': cmp = (a.purchasePrice || 0) - (b.purchasePrice || 0); break;
        case 'date': cmp = new Date(a.purchaseDate || a.createdAt || 0).getTime() - new Date(b.purchaseDate || b.createdAt || 0).getTime(); break;
      }
      return itemSortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [allItems, itemSearch, itemCategory, itemCondition, itemTracked, itemSort, itemSortDir, trackedItemIds]);

  const itemTotalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const pagedItems = filteredItems.slice((itemPage - 1) * ITEMS_PER_PAGE, itemPage * ITEMS_PER_PAGE);

  // ── Transactions tab ──
  const allTransactions = useMemo(() => {
    const txs = assets.flatMap(a =>
      (a.transactions || []).map((tx: any) => ({
        ...tx,
        assetName: a.item?.name || 'Unknown',
        assetId: a.id,
      }))
    );
    // use effectiveDate (the actual field name) with fallback to transactionDate
    txs.sort((a, b) => new Date(b.effectiveDate || b.transactionDate || 0).getTime() - new Date(a.effectiveDate || a.transactionDate || 0).getTime());
    return txs;
  }, [assets]);

  const filteredTx = useMemo(() => {
    let list = [...allTransactions];
    const q = txSearch.toLowerCase().trim();
    if (q) list = list.filter(t =>
      (t.assetName || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      (t.transactionType || '').toLowerCase().includes(q)
    );
    if (txType !== 'all') list = list.filter(t => t.transactionType === txType);
    return list;
  }, [allTransactions, txSearch, txType]);

  const txTotalPages = Math.max(1, Math.ceil(filteredTx.length / ITEMS_PER_PAGE));
  const pagedTx = filteredTx.slice((txPage - 1) * ITEMS_PER_PAGE, txPage * ITEMS_PER_PAGE);

  const txTypes = useMemo(() => {
    const set = new Set<string>();
    allTransactions.forEach(t => { if (t.transactionType) set.add(t.transactionType); });
    return Array.from(set).sort();
  }, [allTransactions]);

  /* ═══════════════  ACTIONS  ═══════════════ */

  const handleCreateAsset = async () => {
    if (!createForm.itemId.trim()) { setError('Item ID is required'); return; }
    if (!createForm.acquisitionCost || Number(createForm.acquisitionCost) <= 0) { setError('Acquisition cost must be greater than 0'); return; }
    try {
      setCreating(true);
      await api.post('/accounting/assets', {
        itemId: createForm.itemId.trim(),
        assetClass: createForm.assetClass || undefined,
        acquisitionCost: Number(createForm.acquisitionCost),
        acquisitionDate: createForm.acquisitionDate,
        salvageValue: Number(createForm.salvageValue) || 0,
        depreciationMethod: createForm.depreciationMethod,
        usefulLifeMonths: Number(createForm.usefulLifeMonths) || 60,
      });
      setShowCreateModal(false);
      setSuccess('Asset account created successfully');
      setCreateForm({ itemId: '', assetClass: '', acquisitionCost: '', acquisitionDate: new Date().toISOString().split('T')[0], salvageValue: '0', depreciationMethod: 'STRAIGHT_LINE', usefulLifeMonths: '60' });
      await fetchAssets();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create asset');
    } finally {
      setCreating(false);
    }
  };

  const handleDepreciate = async (assetId: string) => {
    try {
      await api.post(`/accounting/assets/${assetId}/depreciate`);
      setSuccess('Depreciation processed');
      await fetchAssets();
      if (selectedAsset?.id === assetId) loadDetail(assetId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to process depreciation');
    }
  };

  const handleBatchDepreciate = async () => {
    try {
      const res = await api.post('/accounting/batch-depreciate');
      setSuccess(`Depreciation processed for ${res.data?.processed ?? 0} assets`);
      await fetchAssets();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run batch depreciation');
    }
  };

  const handleImpairment = async () => {
    try {
      await api.post(`/accounting/assets/${modalAssetId}/impairment`, {
        impairmentAmount: Number(impairForm.impairmentAmount),
        reason: impairForm.reason,
        effectiveDate: impairForm.effectiveDate,
      });
      setShowImpairmentModal(false);
      setSuccess('Impairment recorded');
      await fetchAssets();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record impairment');
    }
  };

  const handleRevaluation = async () => {
    try {
      await api.post(`/accounting/assets/${modalAssetId}/revaluation`, {
        newValue: Number(revalForm.newValue),
        appraiserName: revalForm.appraiserName || undefined,
        valuationMethod: revalForm.valuationMethod || undefined,
        notes: revalForm.notes || undefined,
      });
      setShowRevalModal(false);
      setSuccess('Revaluation recorded');
      await fetchAssets();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record revaluation');
    }
  };

  const handleDispose = async () => {
    try {
      await api.post(`/accounting/assets/${modalAssetId}/dispose`, {
        disposalAmount: Number(disposeForm.disposalAmount),
        reason: disposeForm.reason,
        effectiveDate: disposeForm.effectiveDate,
      });
      setShowDisposeModal(false);
      setSelectedAsset(null);
      setSuccess('Asset disposed');
      await fetchAssets();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to dispose asset');
    }
  };

  const loadDetail = async (assetId: string) => {
    try {
      const res = await api.get(`/accounting/assets/${assetId}`);
      setSelectedAsset(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load details');
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/accounting/reports/asset-register');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asset-register-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to export');
    }
  };

  const quickTrack = (item: any) => {
    setCreateForm({
      itemId: item.id,
      assetClass: item.category?.name || '',
      acquisitionCost: String(item.purchasePrice || ''),
      acquisitionDate: item.purchaseDate?.split('T')[0] || new Date().toISOString().split('T')[0],
      salvageValue: '0',
      depreciationMethod: 'STRAIGHT_LINE',
      usefulLifeMonths: '60',
    });
    setShowCreateModal(true);
  };

  const toggleSort = (field: typeof regSort) => {
    if (regSort === field) setRegSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setRegSort(field); setRegSortDir('asc'); }
  };

  const toggleItemSort = (field: typeof itemSort) => {
    if (itemSort === field) setItemSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setItemSort(field); setItemSortDir('asc'); }
  };

  /* ═══════════════  PAGINATION HELPER  ═══════════════ */
  const Pagination = ({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) => {
    if (total <= 1) return null;
    return (
      <div className="acct-pagination">
        <button className="acct-page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <span className="acct-page-info">{page} / {total}</span>
        <button className="acct-page-btn" disabled={page >= total} onClick={() => onPage(page + 1)}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    );
  };

  /* ═══════════════════ RENDER ═══════════════════ */

  if (loading && assets.length === 0) {
    return (
      <div className="accounting">
        <div className="accounting-loading">
          <div className="loading-spinner" />
          <span>Loading accounting data…</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`accounting theme-${theme}`}>
      {/* Messages */}
      {error && (
        <div className="acct-alert error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
          <button className="acct-alert-close" onClick={() => setError(null)}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
      )}
      {success && (
        <div className="acct-alert success">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
          <button className="acct-alert-close" onClick={() => setSuccess(null)}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
      )}

      {/* Header */}
      <div className="acct-header">
        <div className="acct-header-left">
          <h1>
            <FontAwesomeIcon icon={faCalculator} style={{ marginRight: 10, color: '#6366f1' }} />
            {t('accounting.title')}
          </h1>
          <span className="acct-subtitle">{t('accounting.subtitle')}</span>
        </div>
        <div className="acct-header-actions">
          <button className="acct-btn" onClick={handleExport}>
            <FontAwesomeIcon icon={faFileExport} /> {t('action.export')}
          </button>
          <button className="acct-btn" onClick={handleBatchDepreciate}>
            <FontAwesomeIcon icon={faSyncAlt} /> {t('accounting.depreciationReport')}
          </button>
          <button className="acct-btn primary" onClick={() => { setCreateForm({ itemId: '', assetClass: '', acquisitionCost: '', acquisitionDate: new Date().toISOString().split('T')[0], salvageValue: '0', depreciationMethod: 'STRAIGHT_LINE', usefulLifeMonths: '60' }); setShowCreateModal(true); }}>
            <FontAwesomeIcon icon={faPlus} /> {t('accounting.createAsset')}
          </button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="acct-kpi-row">
        <div className="acct-kpi-card">
          <div className="acct-kpi-icon purple"><FontAwesomeIcon icon={faBoxOpen} /></div>
          <div className="acct-kpi-info">
            <div className="acct-kpi-label">{t('accounting.kpi.trackedAssets')}</div>
            <div className="acct-kpi-value">{totalAssets}</div>
            <div className="acct-kpi-sub">{t('accounting.kpi.itemsWithAccounting')}</div>
          </div>
        </div>
        <div className="acct-kpi-card">
          <div className="acct-kpi-icon green"><FontAwesomeIcon icon={faDollarSign} /></div>
          <div className="acct-kpi-info">
            <div className="acct-kpi-label">{t('accounting.kpi.totalBookValue')}</div>
            <div className="acct-kpi-value">{fmt(totalBookValue)}</div>
            <div className="acct-kpi-sub">{t('accounting.kpi.currentNetValue')}</div>
          </div>
        </div>
        <div className="acct-kpi-card">
          <div className="acct-kpi-icon red"><FontAwesomeIcon icon={faArrowDown} /></div>
          <div className="acct-kpi-info">
            <div className="acct-kpi-label">{t('accounting.kpi.accDepreciation')}</div>
            <div className="acct-kpi-value">{fmt(totalDepreciation)}</div>
            <div className="acct-kpi-sub">{totalCost > 0 ? pct((totalDepreciation / totalCost) * 100) : '0.0%'} {t('accounting.kpi.ofCost')}</div>
          </div>
        </div>
        <div className="acct-kpi-card">
          <div className="acct-kpi-icon blue"><FontAwesomeIcon icon={faChartLine} /></div>
          <div className="acct-kpi-info">
            <div className="acct-kpi-label">{t('accounting.kpi.acquisitionCost')}</div>
            <div className="acct-kpi-value">{fmt(totalCost)}</div>
            <div className="acct-kpi-sub">{t('accounting.kpi.originalInvestment')}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="acct-tabs">
        <button className={`acct-tab ${tab === 'register' ? 'active' : ''}`} onClick={() => setTab('register')}>
          <FontAwesomeIcon icon={faReceipt} /> {t('accounting.tabs.register')}
        </button>
        <button className={`acct-tab ${tab === 'items' ? 'active' : ''}`} onClick={() => setTab('items')}>
          <FontAwesomeIcon icon={faBoxOpen} /> {t('accounting.tabs.items')}
        </button>
        <button className={`acct-tab ${tab === 'transactions' ? 'active' : ''}`} onClick={() => setTab('transactions')}>
          <FontAwesomeIcon icon={faBalanceScale} /> {t('accounting.tabs.transactions')}
        </button>
      </div>

      {/* ═══════════════  TAB: Asset Register  ═══════════════ */}
      {tab === 'register' && (
        <div className="acct-table-wrap">
          {/* Toolbar */}
          <div className="acct-toolbar">
            <div className="acct-search-box">
              <FontAwesomeIcon icon={faSearch} className="acct-search-icon" />
              <input
                className="acct-search-input"
                placeholder={t('accounting.searchAssetsPlaceholder')}
                value={regSearch}
                onChange={e => setRegSearch(e.target.value)}
              />
              {regSearch && (
                <button className="acct-search-clear" onClick={() => setRegSearch('')}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            <div className="acct-toolbar-filters">
              <select className="acct-filter-select" value={regStatus} onChange={e => setRegStatus(e.target.value)}>
                <option value="all">{t('accounting.allStatus')}</option>
                <option value="ACTIVE">{t('accounting.active')}</option>
                <option value="DISPOSED">{t('accounting.disposed')}</option>
                <option value="IMPAIRED">{t('accounting.impaired')}</option>
                <option value="WRITTEN_OFF">{t('accounting.writtenOff')}</option>
                <option value="HELD_FOR_SALE">{t('accounting.heldForSale')}</option>
              </select>
              <select className="acct-filter-select" value={regMethod} onChange={e => setRegMethod(e.target.value)}>
                <option value="all">{t('accounting.allMethods')}</option>
                {Object.entries(METHOD_KEYS).map(([k, v]) => <option key={k} value={k}>{t(v as any)}</option>)}
              </select>
              {assetClasses.length > 0 && (
                <select className="acct-filter-select" value={regClass} onChange={e => setRegClass(e.target.value)}>
                  <option value="all">{t('accounting.allClasses')}</option>
                  {assetClasses.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </div>
          </div>

          <div className="acct-table-header">
            <span className="acct-table-title">
              {filteredAssets.length === assets.length
                ? t('accounting.trackedAssetCount').replace('{count}', String(assets.length))
                : t('accounting.filteredAssets').replace('{filtered}', String(filteredAssets.length)).replace('{total}', String(assets.length))}
            </span>
            <Pagination page={regPage} total={regTotalPages} onPage={setRegPage} />
          </div>

          {filteredAssets.length === 0 ? (
            <div className="acct-empty">
              <div className="acct-empty-icon"><FontAwesomeIcon icon={faCalculator} /></div>
              <h3>{assets.length === 0 ? t('accounting.noTrackedYet') : t('accounting.noAssetsMatch')}</h3>
              <p>
                {assets.length === 0
                  ? t('accounting.startTracking')
                  : t('accounting.adjustFilters')}
              </p>
              {assets.length === 0 ? (
                <button className="acct-btn primary" onClick={() => setTab('items')}>
                  <FontAwesomeIcon icon={faBoxOpen} /> {t('accounting.browseItems')}
                </button>
              ) : (
                <button className="acct-btn" onClick={() => { setRegSearch(''); setRegStatus('all'); setRegMethod('all'); setRegClass('all'); }}>
                  <FontAwesomeIcon icon={faTimes} /> {t('accounting.clearFilters')}
                </button>
              )}
            </div>
          ) : (
            <>
              <table className="acct-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => toggleSort('name')}>
                      {t('accounting.itemCol')} {regSort === 'name' && <FontAwesomeIcon icon={regSortDir === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon" />}
                    </th>
                    <th className="right sortable" onClick={() => toggleSort('cost')}>
                      {t('accounting.acquisitionCol')} {regSort === 'cost' && <FontAwesomeIcon icon={regSortDir === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon" />}
                    </th>
                    <th className="right sortable" onClick={() => toggleSort('book')}>
                      {t('accounting.bookValueCol')} {regSort === 'book' && <FontAwesomeIcon icon={regSortDir === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon" />}
                    </th>
                    <th className="sortable" onClick={() => toggleSort('dep')}>
                      {t('accounting.depreciationCol')} {regSort === 'dep' && <FontAwesomeIcon icon={regSortDir === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon" />}
                    </th>
                    <th>{t('accounting.methodCol')}</th>
                    <th>{t('accounting.statusCol')}</th>
                    <th>{t('accounting.actionsCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedAssets.map(asset => {
                    const dp = depPct(asset);
                    const thumb = getItemThumb(asset.item);
                    return (
                      <tr key={asset.id}>
                        <td>
                          <div className="acct-item-name">
                            {thumb ? (
                              <img src={thumb} alt="" className="acct-item-thumb" />
                            ) : (
                              <div className="acct-item-thumb-placeholder">
                                <FontAwesomeIcon icon={faBoxOpen} />
                              </div>
                            )}
                            <div className="acct-item-details">
                              <div className="acct-item-title">{asset.item?.name || t('accounting.unnamed')}</div>
                              <div className="acct-item-class">{asset.assetClass || t('accounting.unclassified')} · #{asset.assetNumber || asset.id.slice(0, 8)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="right">
                          <span className="acct-value neutral">{fmt(asset.acquisitionCost)}</span>
                        </td>
                        <td className="right">
                          <span className={`acct-value ${asset.bookValue < (asset.salvageValue || 0) ? 'negative' : 'positive'}`}>
                            {fmt(asset.bookValue)}
                          </span>
                        </td>
                        <td>
                          <div className="acct-dep-bar-wrap">
                            <div className="acct-dep-bar">
                              <div className={`acct-dep-bar-fill ${depClass(dp)}`} style={{ width: `${dp}%` }} />
                            </div>
                            <div className="acct-dep-label">{pct(dp)} · {fmt(asset.accumulatedDepreciation)}</div>
                          </div>
                        </td>
                        <td>
                          <span className="acct-badge method">{METHOD_KEYS[asset.depreciationMethod] ? t(METHOD_KEYS[asset.depreciationMethod] as any) : asset.depreciationMethod}</span>
                        </td>
                        <td>
                          <span className={`acct-badge ${STATUS_CLASS[asset.status] || ''}`}>{asset.status}</span>
                        </td>
                        <td>
                          <div className="acct-actions">
                            <button className="acct-action-btn" title={t('accounting.viewDetail')} onClick={() => loadDetail(asset.id)}>
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button className="acct-action-btn" title={t('accounting.depreciate')} onClick={() => handleDepreciate(asset.id)}>
                              <FontAwesomeIcon icon={faPlay} />
                            </button>
                            <button className="acct-action-btn" title={t('accounting.revalueAction')} onClick={() => { setModalAssetId(asset.id); setShowRevalModal(true); }}>
                              <FontAwesomeIcon icon={faGavel} />
                            </button>
                            <button className="acct-action-btn danger" title={t('accounting.disposeAction')} onClick={() => { setModalAssetId(asset.id); setShowDisposeModal(true); }}>
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="acct-table-footer">
                <span className="acct-table-count">{t('accounting.showing')} {(regPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(regPage * ITEMS_PER_PAGE, filteredAssets.length)} {t('accounting.of')} {filteredAssets.length}</span>
                <Pagination page={regPage} total={regTotalPages} onPage={setRegPage} />
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════════════  TAB: All Items  ═══════════════ */}
      {tab === 'items' && (
        <div className="acct-table-wrap">
          {/* Toolbar */}
          <div className="acct-toolbar">
            <div className="acct-search-box">
              <FontAwesomeIcon icon={faSearch} className="acct-search-icon" />
              <input
                className="acct-search-input"
                placeholder={t('accounting.searchItemsPlaceholder')}
                value={itemSearch}
                onChange={e => setItemSearch(e.target.value)}
              />
              {itemSearch && (
                <button className="acct-search-clear" onClick={() => setItemSearch('')}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            <div className="acct-toolbar-filters">
              {categories.length > 0 && (
                <select className="acct-filter-select" value={itemCategory} onChange={e => setItemCategory(e.target.value)}>
                  <option value="all">{t('accounting.allCategories')}</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
              <select className="acct-filter-select" value={itemTracked} onChange={e => setItemTracked(e.target.value as any)}>
                <option value="all">{t('accounting.allItemsFilter')}</option>
                <option value="tracked">{t('accounting.trackedOnly')}</option>
                <option value="untracked">{t('accounting.untrackedOnly')}</option>
              </select>
              <select className="acct-filter-select" value={itemCondition} onChange={e => setItemCondition(e.target.value)}>
                <option value="all">{t('accounting.allConditions') || 'All Conditions'}</option>
                <option value="NEW">{t('condition.new')}</option>
                <option value="LIKE_NEW">{t('condition.likeNew')}</option>
                <option value="EXCELLENT">{t('condition.excellent')}</option>
                <option value="GOOD">{t('condition.good')}</option>
                <option value="FAIR">{t('condition.fair')}</option>
                <option value="POOR">{t('condition.poor')}</option>
              </select>
            </div>
          </div>

          <div className="acct-table-header">
            <span className="acct-table-title">
              {filteredItems.length === allItems.length
                ? t('accounting.yourItemsCount').replace('{count}', String(allItems.length))
                : t('accounting.filteredItems').replace('{filtered}', String(filteredItems.length)).replace('{total}', String(allItems.length))}
            </span>
            <Pagination page={itemPage} total={itemTotalPages} onPage={setItemPage} />
          </div>

          {filteredItems.length === 0 ? (
            <div className="acct-empty">
              <div className="acct-empty-icon"><FontAwesomeIcon icon={faBoxOpen} /></div>
              <h3>{allItems.length === 0 ? t('accounting.noItemsInLib') : t('accounting.noItemsMatch')}</h3>
              <p>
                {allItems.length === 0
                  ? t('accounting.addItemsFirst')
                  : t('accounting.adjustFilters')}
              </p>
              {allItems.length > 0 && (
                <button className="acct-btn" onClick={() => { setItemSearch(''); setItemCategory('all'); setItemTracked('all'); }}>
                  <FontAwesomeIcon icon={faTimes} /> {t('accounting.clearFilters')}
                </button>
              )}
            </div>
          ) : (
            <>
              <table className="acct-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => toggleItemSort('name')}>
                      {t('accounting.itemCol')} {itemSort === 'name' && <FontAwesomeIcon icon={itemSortDir === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon" />}
                    </th>
                    <th>
                      <FontAwesomeIcon icon={faTag} style={{ marginRight: 4 }} /> {t('accounting.categoryCol')}
                    </th>
                    <th className="right sortable" onClick={() => toggleItemSort('price')}>
                      {t('accounting.purchasePriceCol')} {itemSort === 'price' && <FontAwesomeIcon icon={itemSortDir === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon" />}
                    </th>
                    <th className="sortable" onClick={() => toggleItemSort('date')}>
                      {t('accounting.dateCol')} {itemSort === 'date' && <FontAwesomeIcon icon={itemSortDir === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon" />}
                    </th>
                    <th>{t('accounting.trackingCol')}</th>
                    <th>{t('accounting.actionCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map(item => {
                    const tracked = trackedItemIds.has(item.id);
                    const thumb = getItemThumb(item);
                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="acct-item-name">
                            {thumb ? (
                              <img src={thumb} alt="" className="acct-item-thumb" />
                            ) : (
                              <div className="acct-item-thumb-placeholder">
                                <FontAwesomeIcon icon={faBoxOpen} />
                              </div>
                            )}
                            <div className="acct-item-details">
                              <div className="acct-item-title">{item.name}</div>
                              <div className="acct-item-class">{item.condition || t('accounting.unknown')}{item.location ? ` · ${item.location}` : ''}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {item.category?.name ? (
                            <span className="acct-badge method">
                              <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: 4 }} />
                              {item.category.name}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="right">
                          <span className="acct-value neutral">{item.purchasePrice ? fmt(item.purchasePrice) : '—'}</span>
                        </td>
                        <td>{dateStr(item.purchaseDate || item.createdAt)}</td>
                        <td>
                          {tracked ? (
                            <span className="acct-badge active">
                              <FontAwesomeIcon icon={faCheckCircle} /> {t('accounting.tracked')}
                            </span>
                          ) : (
                            <span className="acct-badge disposed">{t('accounting.notTracked')}</span>
                          )}
                        </td>
                        <td>
                          {tracked ? (
                            <button className="acct-btn sm" onClick={() => { const a = assets.find(x => x.itemId === item.id); if (a) loadDetail(a.id); }}>
                              <FontAwesomeIcon icon={faEye} /> {t('accounting.viewDetail')}
                            </button>
                          ) : (
                            <button className="acct-btn primary sm" onClick={() => quickTrack(item)}>
                              <FontAwesomeIcon icon={faPlus} /> {t('accounting.trackItem')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="acct-table-footer">
                <span className="acct-table-count">{t('accounting.showing')} {(itemPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(itemPage * ITEMS_PER_PAGE, filteredItems.length)} {t('accounting.of')} {filteredItems.length}</span>
                <Pagination page={itemPage} total={itemTotalPages} onPage={setItemPage} />
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════════════  TAB: Transactions  ═══════════════ */}
      {tab === 'transactions' && (
        <div className="acct-table-wrap">
          <div className="acct-toolbar">
            <div className="acct-search-box">
              <FontAwesomeIcon icon={faSearch} className="acct-search-icon" />
              <input
                className="acct-search-input"
                placeholder={t('accounting.searchTxPlaceholder')}
                value={txSearch}
                onChange={e => setTxSearch(e.target.value)}
              />
              {txSearch && (
                <button className="acct-search-clear" onClick={() => setTxSearch('')}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            <div className="acct-toolbar-filters">
              {txTypes.length > 0 && (
                <select className="acct-filter-select" value={txType} onChange={e => setTxType(e.target.value)}>
                  <option value="all">{t('accounting.allTypes')}</option>
                  {txTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                </select>
              )}
            </div>
          </div>

          <div className="acct-table-header">
            <span className="acct-table-title">
              {filteredTx.length === allTransactions.length
                ? t('accounting.txCount').replace('{count}', String(allTransactions.length))
                : t('accounting.filteredTx').replace('{filtered}', String(filteredTx.length)).replace('{total}', String(allTransactions.length))}
            </span>
            <Pagination page={txPage} total={txTotalPages} onPage={setTxPage} />
          </div>

          {filteredTx.length === 0 ? (
            <div className="acct-empty">
              <div className="acct-empty-icon"><FontAwesomeIcon icon={faBalanceScale} /></div>
              <h3>{allTransactions.length === 0 ? t('accounting.noTxYet') : t('accounting.noTxMatch')}</h3>
              <p>
                {allTransactions.length === 0
                  ? t('accounting.startDepreciation')
                  : t('accounting.adjustTxFilter')}
              </p>
              {allTransactions.length > 0 && (
                <button className="acct-btn" onClick={() => { setTxSearch(''); setTxType('all'); }}>
                  <FontAwesomeIcon icon={faTimes} /> {t('accounting.clearFilters')}
                </button>
              )}
            </div>
          ) : (
            <>
              <table className="acct-table">
                <thead>
                  <tr>
                    <th>{t('accounting.txDateCol')}</th>
                    <th>{t('accounting.txAssetCol')}</th>
                    <th>{t('accounting.txTypeCol')}</th>
                    <th>{t('accounting.txDescCol')}</th>
                    <th className="right">{t('accounting.txAmountCol')}</th>
                    <th className="right">{t('accounting.bookValueAfterCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedTx.map((tx: any, idx: number) => (
                    <tr key={tx.id || idx}>
                      <td>{dateStr(tx.effectiveDate || tx.transactionDate)}</td>
                      <td>{tx.assetName}</td>
                      <td><span className="acct-badge method">{(tx.transactionType || '').replace(/_/g, ' ')}</span></td>
                      <td>{tx.description || '—'}</td>
                      <td className="right">
                        <span className={`acct-value ${(tx.debitAmount || 0) > 0 ? 'negative' : 'positive'}`}>
                          {(tx.debitAmount || 0) > 0 ? `-${fmt(tx.debitAmount)}` : `+${fmt(tx.creditAmount)}`}
                        </span>
                      </td>
                      <td className="right"><span className="acct-value neutral">{fmt(tx.bookValueAfter)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="acct-table-footer">
                <span className="acct-table-count">{t('accounting.showing')} {(txPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(txPage * ITEMS_PER_PAGE, filteredTx.length)} {t('accounting.of')} {filteredTx.length}</span>
                <Pagination page={txPage} total={txTotalPages} onPage={setTxPage} />
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════════════  DETAIL MODAL  ═══════════════ */}
      {selectedAsset && (
        <div className="acct-modal-overlay" onClick={() => setSelectedAsset(null)}>
          <div className="acct-modal lg" onClick={e => e.stopPropagation()}>
            <div className="acct-modal-header">
              <h2>{selectedAsset.item?.name || t('accounting.assetDetails')}</h2>
              <button className="acct-modal-close" onClick={() => setSelectedAsset(null)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="acct-modal-body">
              <div className="acct-detail-grid">
                <div className="acct-detail-card">
                  <h4>{t('accounting.valueInfo')}</h4>
                  <div className="acct-detail-row">
                    <span className="acct-detail-label">{t('accounting.acquisitionCost')}</span>
                    <span className="acct-detail-value">{fmt(selectedAsset.acquisitionCost)}</span>
                  </div>
                  <div className="acct-detail-row">
                    <span className="acct-detail-label">{t('accounting.bookValue')}</span>
                    <span className="acct-detail-value" style={{ color: '#6ee7b7' }}>{fmt(selectedAsset.bookValue)}</span>
                  </div>
                  <div className="acct-detail-row">
                    <span className="acct-detail-label">{t('accounting.accDep')}</span>
                    <span className="acct-detail-value" style={{ color: '#fca5a5' }}>{fmt(selectedAsset.accumulatedDepreciation)}</span>
                  </div>
                  <div className="acct-detail-row">
                    <span className="acct-detail-label">{t('accounting.salvageValue')}</span>
                    <span className="acct-detail-value">{fmt(selectedAsset.salvageValue)}</span>
                  </div>
                  {selectedAsset.fairMarketValue != null && (
                    <div className="acct-detail-row">
                      <span className="acct-detail-label">{t('accounting.fairMarketValue')}</span>
                      <span className="acct-detail-value">{fmt(selectedAsset.fairMarketValue)}</span>
                    </div>
                  )}
                </div>
                <div className="acct-detail-card">
                  <h4>{t('accounting.depInfo')}</h4>
                  <div className="acct-detail-row">
                    <span className="acct-detail-label">{t('accounting.depreciationMethod')}</span>
                    <span className="acct-detail-value">{METHOD_KEYS[selectedAsset.depreciationMethod] ? t(METHOD_KEYS[selectedAsset.depreciationMethod] as any) : selectedAsset.depreciationMethod}</span>
                  </div>
                  <div className="acct-detail-row">
                    <span className="acct-detail-label">{t('accounting.usefulLifeMonths')}</span>
                    <span className="acct-detail-value">{selectedAsset.usefulLifeMonths} {t('accounting.months')}</span>
                  </div>
                  {(selectedAsset.depreciationInfo || selectedAsset.depreciation) && (() => {
                    const dep = selectedAsset.depreciationInfo || selectedAsset.depreciation;
                    return (
                      <>
                        <div className="acct-detail-row">
                          <span className="acct-detail-label">{t('accounting.monthly')}</span>
                          <span className="acct-detail-value">{fmt(dep?.monthlyDepreciation)}</span>
                        </div>
                        <div className="acct-detail-row">
                          <span className="acct-detail-label">{t('accounting.remainingMonths')}</span>
                          <span className="acct-detail-value">{dep?.remainingLifeMonths ?? 0} {t('accounting.months')}</span>
                        </div>
                        <div className="acct-detail-row">
                          <span className="acct-detail-label">{t('accounting.depPercent')}</span>
                          <span className="acct-detail-value">{pct(dep?.percentDepreciated)}</span>
                        </div>
                      </>
                    );
                  })()}
                  <div style={{ marginTop: 12 }}>
                    <div className="acct-dep-bar">
                      <div className={`acct-dep-bar-fill ${depClass(depPct(selectedAsset))}`} style={{ width: `${depPct(selectedAsset)}%` }} />
                    </div>
                  </div>
                </div>
              </div>

              {selectedAsset.transactions && selectedAsset.transactions.length > 0 && (
                <>
                  <h4 style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('accounting.recentTx')}</h4>
                  <div className="acct-detail-card" style={{ marginBottom: 0 }}>
                    {selectedAsset.transactions.slice(0, 8).map((tx: any, i: number) => (
                      <div className="acct-detail-row" key={tx.id || i}>
                        <span className="acct-detail-label">{dateStr(tx.effectiveDate || tx.transactionDate)} · {(tx.transactionType || '').replace(/_/g, ' ')}</span>
                        <span className="acct-detail-value">{(tx.debitAmount || 0) > 0 ? fmt(tx.debitAmount) : fmt(tx.creditAmount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="acct-modal-footer">
              <button className="acct-btn" onClick={() => handleDepreciate(selectedAsset.id)}>
                <FontAwesomeIcon icon={faPlay} /> {t('accounting.depreciate')}
              </button>
              <button className="acct-btn" onClick={() => { setModalAssetId(selectedAsset.id); setShowImpairmentModal(true); }}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {t('accounting.impairmentAction')}
              </button>
              <button className="acct-btn" onClick={() => { setModalAssetId(selectedAsset.id); setShowRevalModal(true); }}>
                <FontAwesomeIcon icon={faGavel} /> {t('accounting.revalueAction')}
              </button>
              <button className="acct-btn" style={{ color: '#fca5a5' }} onClick={() => { setModalAssetId(selectedAsset.id); setShowDisposeModal(true); }}>
                <FontAwesomeIcon icon={faBan} /> {t('accounting.disposeAction')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════  CREATE MODAL  ═══════════════ */}
      {showCreateModal && (
        <div className="acct-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="acct-modal" onClick={e => e.stopPropagation()}>
            <div className="acct-modal-header">
              <h2>{t('accounting.trackAsset')}</h2>
              <button className="acct-modal-close" onClick={() => setShowCreateModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="acct-modal-body">
              {!createForm.itemId && (
                <div className="acct-alert info" style={{ marginBottom: 16 }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{t('accounting.tipAutoFill')}</span>
                </div>
              )}
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.itemIdLabel')}</label>
                <input className="acct-form-input" value={createForm.itemId} onChange={e => setCreateForm({ ...createForm, itemId: e.target.value })} placeholder={t('accounting.pasteItemId')} />
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.assetClass')}</label>
                <select className="acct-form-select" value={createForm.assetClass} onChange={e => setCreateForm({ ...createForm, assetClass: e.target.value })}>
                  <option value="">{t('accounting.selectClassOption')}</option>
                  {[
                    { key: 'Buildings', label: t('accounting.classBuildings') },
                    { key: 'Machinery', label: t('accounting.classMachinery') },
                    { key: 'Equipment', label: t('accounting.classEquipment') },
                    { key: 'Vehicles', label: t('accounting.classVehicles') },
                    { key: 'Furniture', label: t('accounting.classFurniture') },
                    { key: 'Computers', label: t('accounting.classComputers') },
                    { key: 'Software', label: t('accounting.classSoftware') },
                    { key: 'Collectibles', label: t('accounting.classCollectibles') },
                    { key: 'Art', label: t('accounting.classArt') },
                    { key: 'Jewelry', label: t('accounting.classJewelry') },
                    { key: 'Real Estate', label: t('accounting.classRealEstate') },
                    { key: 'Investments', label: t('accounting.classInvestments') },
                    { key: 'Other', label: t('accounting.classOther') },
                  ].map(c => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="acct-form-row">
                <div className="acct-form-group">
                  <label className="acct-form-label">{t('accounting.acquisitionCost')} *</label>
                  <input className="acct-form-input" type="number" min="0" step="0.01" value={createForm.acquisitionCost} onChange={e => setCreateForm({ ...createForm, acquisitionCost: e.target.value })} placeholder="0.00" />
                </div>
                <div className="acct-form-group">
                  <label className="acct-form-label">{t('accounting.acquisitionDate')}</label>
                  <input className="acct-form-input" type="date" value={createForm.acquisitionDate} onChange={e => setCreateForm({ ...createForm, acquisitionDate: e.target.value })} />
                </div>
              </div>
              <div className="acct-form-row">
                <div className="acct-form-group">
                  <label className="acct-form-label">{t('accounting.salvageValue')}</label>
                  <input className="acct-form-input" type="number" min="0" step="0.01" value={createForm.salvageValue} onChange={e => setCreateForm({ ...createForm, salvageValue: e.target.value })} />
                  <span className="acct-form-hint">{t('accounting.expectedValue')}</span>
                </div>
                <div className="acct-form-group">
                  <label className="acct-form-label">{t('accounting.usefulLifeMonths')}</label>
                  <input className="acct-form-input" type="number" min="1" value={createForm.usefulLifeMonths} onChange={e => setCreateForm({ ...createForm, usefulLifeMonths: e.target.value })} />
                </div>
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.depreciationMethod')}</label>
                <select className="acct-form-select" value={createForm.depreciationMethod} onChange={e => setCreateForm({ ...createForm, depreciationMethod: e.target.value })}>
                  {Object.entries(METHOD_KEYS).map(([k, v]) => <option key={k} value={k}>{t(v as any)}</option>)}
                </select>
              </div>
            </div>
            <div className="acct-modal-footer">
              <button className="acct-btn" onClick={() => setShowCreateModal(false)}>{t('accounting.cancel')}</button>
              <button className="acct-btn primary" onClick={handleCreateAsset} disabled={creating}>
                {creating ? t('accounting.creating') : t('accounting.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════  IMPAIRMENT MODAL  ═══════════════ */}
      {showImpairmentModal && (
        <div className="acct-modal-overlay" onClick={() => setShowImpairmentModal(false)}>
          <div className="acct-modal" onClick={e => e.stopPropagation()}>
            <div className="acct-modal-header">
              <h2>{t('accounting.recordImpairment')}</h2>
              <button className="acct-modal-close" onClick={() => setShowImpairmentModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="acct-modal-body">
              <div className="acct-alert warning" style={{ marginBottom: 16 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{t('accounting.impairmentDesc')}</span>
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.impairmentAmountLabel')}</label>
                <input className="acct-form-input" type="number" min="0" step="0.01" value={impairForm.impairmentAmount} onChange={e => setImpairForm({ ...impairForm, impairmentAmount: e.target.value })} />
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.reasonLabel')}</label>
                <input className="acct-form-input" value={impairForm.reason} onChange={e => setImpairForm({ ...impairForm, reason: e.target.value })} placeholder={t('accounting.reasonPlaceholder')} />
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.effectiveDateLabel')}</label>
                <input className="acct-form-input" type="date" value={impairForm.effectiveDate} onChange={e => setImpairForm({ ...impairForm, effectiveDate: e.target.value })} />
              </div>
            </div>
            <div className="acct-modal-footer">
              <button className="acct-btn" onClick={() => setShowImpairmentModal(false)}>{t('accounting.cancel')}</button>
              <button className="acct-btn primary" onClick={handleImpairment}>{t('accounting.recordImpairment')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════  REVALUATION MODAL  ═══════════════ */}
      {showRevalModal && (
        <div className="acct-modal-overlay" onClick={() => setShowRevalModal(false)}>
          <div className="acct-modal" onClick={e => e.stopPropagation()}>
            <div className="acct-modal-header">
              <h2>{t('accounting.recordRevaluation')}</h2>
              <button className="acct-modal-close" onClick={() => setShowRevalModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="acct-modal-body">
              <div className="acct-alert info" style={{ marginBottom: 16 }}>
                <FontAwesomeIcon icon={faGavel} />
                <span>{t('accounting.revalDesc')}</span>
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.newAppraisedValue')}</label>
                <input className="acct-form-input" type="number" min="0" step="0.01" value={revalForm.newValue} onChange={e => setRevalForm({ ...revalForm, newValue: e.target.value })} />
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.appraiserName')}</label>
                <input className="acct-form-input" value={revalForm.appraiserName} onChange={e => setRevalForm({ ...revalForm, appraiserName: e.target.value })} placeholder={t('accounting.optional')} />
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.valuationMethodLabel')}</label>
                <input className="acct-form-input" value={revalForm.valuationMethod} onChange={e => setRevalForm({ ...revalForm, valuationMethod: e.target.value })} placeholder={t('accounting.valuationMethodPlaceholder')} />
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.notesLabel')}</label>
                <input className="acct-form-input" value={revalForm.notes} onChange={e => setRevalForm({ ...revalForm, notes: e.target.value })} placeholder={t('accounting.optionalNotes')} />
              </div>
            </div>
            <div className="acct-modal-footer">
              <button className="acct-btn" onClick={() => setShowRevalModal(false)}>{t('accounting.cancel')}</button>
              <button className="acct-btn primary" onClick={handleRevaluation}>{t('accounting.recordRevaluation')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════  DISPOSE MODAL  ═══════════════ */}
      {showDisposeModal && (
        <div className="acct-modal-overlay" onClick={() => setShowDisposeModal(false)}>
          <div className="acct-modal" onClick={e => e.stopPropagation()}>
            <div className="acct-modal-header">
              <h2>{t('accounting.disposeAsset')}</h2>
              <button className="acct-modal-close" onClick={() => setShowDisposeModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="acct-modal-body">
              <div className="acct-alert error" style={{ marginBottom: 16 }}>
                <FontAwesomeIcon icon={faBan} />
                <span>{t('accounting.disposeDesc')}</span>
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.disposalAmountLabel')}</label>
                <input className="acct-form-input" type="number" min="0" step="0.01" value={disposeForm.disposalAmount} onChange={e => setDisposeForm({ ...disposeForm, disposalAmount: e.target.value })} />
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.reasonSelect')}</label>
                <select className="acct-form-select" value={disposeForm.reason} onChange={e => setDisposeForm({ ...disposeForm, reason: e.target.value })}>
                  <option value="SOLD">{t('accounting.sold')}</option>
                  <option value="DONATED">{t('accounting.donated')}</option>
                  <option value="SCRAPPED">{t('accounting.scrapped')}</option>
                  <option value="STOLEN">{t('accounting.stolenLost')}</option>
                  <option value="TRADED_IN">{t('accounting.tradedIn')}</option>
                  <option value="OTHER">{t('accounting.otherReason')}</option>
                </select>
              </div>
              <div className="acct-form-group">
                <label className="acct-form-label">{t('accounting.effectiveDateLabel')}</label>
                <input className="acct-form-input" type="date" value={disposeForm.effectiveDate} onChange={e => setDisposeForm({ ...disposeForm, effectiveDate: e.target.value })} />
              </div>
            </div>
            <div className="acct-modal-footer">
              <button className="acct-btn" onClick={() => setShowDisposeModal(false)}>{t('accounting.cancel')}</button>
              <button className="acct-btn primary" style={{ background: '#ef4444', borderColor: '#ef4444' }} onClick={handleDispose}>{t('accounting.confirmDisposal')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
